// ── SPEND ANALYTICS VISIBILITY ─────────────────────────────────────────────

export const getSpendAnalyticsCardVisibilityFromEnv = () => {
  const value =
    typeof window !== "undefined" ? window.SHOW_SPEND_ANALYTICS_CARD : null;

  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;

  const normalized = String(value).trim().toLowerCase();
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  if (["true", "1", "yes", "on"].includes(normalized)) return true;

  return Boolean(value);
};

// ── SESSION HISTORY ─────────────────────────────────────────────────────────

export const normalizeSessionHistory = (payload) => {
  if (!Array.isArray(payload)) return [];
  return [...payload]
    .filter((item) => item && typeof item === 'object' && item.sessionId)
    .map((item) => ({
      sessionId: item.sessionId,
      conversationId: item.conversationId || '',
      title: item.title || 'Untitled session',
      lastMessage: item.last_message || '',
      messageCount: Number(item.messageCount) || 0,
      isPinned: Boolean(item.isPinned),
      createdAt: item.createdAt || null,
      lastActiveAt: item.lastActiveAt || null,
    }))
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
      const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
      return bTime - aTime;
    });
};

export const mapSessionSummaryItem = (item) => {
  if (!item || typeof item !== 'object' || !item.session_id) {
    return null;
  }
  return {
    title: item.title || 'Untitled session',
    sessionId: item.session_id,
    conversationId: item.conversation_id || '',
    isPinned: Boolean(item.is_pinned),
    updatedAt: item.updated_at || null,
    pinnedAt: item.pinned_at || null,
    lastActiveAt: item.last_active_at || null,
    createdAt: item.created_at || null,
    lastMessage: item.last_message || '',
    messageCount: Number(item.message_count) || 0,
  };
};

// ── INSIGHTS EXTRACTION ─────────────────────────────────────────────────────

const isAlphaChar = (ch) => (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z');

const normalizeInsightsHeading = (line) => {
  let value = (line || '').trim();
  if (value.startsWith('-')) {
    value = value.slice(1).trim();
  }
  if (value.startsWith('**')) {
    value = value.slice(2);
  }
  if (value.endsWith('**')) {
    value = value.slice(0, -2);
  }
  return value.trim();
};

const isInsightsHeadingLine = (line) => {
  const normalized = normalizeInsightsHeading(line).toLowerCase();

  return normalized.startsWith('insight:') || normalized.startsWith('insights:');
};

const isLikelySectionHeadingLine = (line) => {
  const t = (line || '').trim();
  if (!t) {
    return false;
  }
  if (t.startsWith('- ') || t.startsWith('* ')) {
    return false;
  }
  if (t.startsWith('**') && t.endsWith('**') && t.length > 4) {
    const inner = t.slice(2, -2).trim();
    const colonIdx = inner.indexOf(':');
    return colonIdx > 0 && isAlphaChar(inner[0]);
  }
  const colonIdx = t.indexOf(':');
  if (colonIdx <= 0) {
    return false;
  }
  return isAlphaChar(t[0]);
};

export const extractInsightsBlock = (response) => {
  if (!response) {
    return { insight: null, responseWithoutInsights: '' };
  }
  const lines = response.split('\n');
  let endIdx = -1;
  const insightLines = [];
  let startIdx = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (!isInsightsHeadingLine(lines[i])) {
      continue;
    }
    startIdx = i;
    const headingLine = normalizeInsightsHeading(lines[i]);
    const colonIdx = headingLine.indexOf(':');
    const firstLine = colonIdx >= 0 ? 
                        headingLine.slice(colonIdx + 1).trim() : 
                        '';
    if (firstLine) insightLines.push(firstLine);
    let j = i + 1;
    while (j < lines.length && !isLikelySectionHeadingLine(lines[j])) {
      insightLines.push(lines[j]);
      j += 1;
    }
    endIdx = j;
    break;
  }
  if (startIdx === -1 || endIdx === -1) {
    return { insight: null, responseWithoutInsights: response };
  }
  const remainingLines = [...lines.slice(0, startIdx), ...lines.slice(endIdx)];
  const responseWithoutInsights = remainingLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  let insightText = insightLines.join('\n').trim() || null;
  if (insightText) {
    // insightText = insightText.replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');
    insightText = insightText.replace(/^\*\*\s{0,10}/, "").replace(/\s{0,10}\*\*$/, "");
  }
  return { insight: insightText, responseWithoutInsights };
};

// ── SESSION MESSAGES ────────────────────────────────────────────────────────

export const normalizeSessionMessages = (payload) => {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((item) => item && typeof item === 'object')
    .map((item, idx) => {
      const role = typeof item.role === 'string' ? item.role.toLowerCase() : '';
      const createdAt = item.created_at || item.createdAt || null;

      if (role === 'user') {
        return {
          text: item.content || item.raw_query || '',
          type: 'user',
          queryId: item.query_id || item.id || null,
          id: item.query_id || item.id || `user-${idx}`,
          createdAt,
        };
      }

      if (role === 'assistant') {
        const responseData = item.response_data && typeof item.response_data === 'object' ? item.response_data : {};
        const rawResponse = item.content || responseData.response || item.response || item.response_data || '';
        const { insight, responseWithoutInsights } = extractInsightsBlock(rawResponse);
        const sqlQueriesRaw = Array.isArray(item.sql_queries)
          ? item.sql_queries
          : Array.isArray(responseData.sql_queries)
            ? responseData.sql_queries
            : [];
        const sqlQueries = sqlQueriesRaw
          .filter((q) => typeof q === 'string' && q.trim())
          .map((q) => q.trim());
        const followUps = Array.isArray(item.follow_up_questions)
          ? item.follow_up_questions
          : Array.isArray(responseData.follow_up_questions)
            ? responseData.follow_up_questions
            : [];
        const citationsRaw = Array.isArray(item.citations)
          ? item.citations
          : Array.isArray(responseData.citations)
            ? responseData.citations
            : [];
        const citations = citationsRaw
          .filter((c) => c && typeof c === 'object')
          .map((c) => ({ file_name: c.file_name || '', url: c.url || c.source || '' }));
        return {
          id: item.query_id || item.id || `assistant-${idx}`,
          queryId: item.query_id || item.id || null,
          messageId: item.message_id || null,
          assistant_message_id: item.id || null,
          type: 'ai',
          lastRefreshed: item.last_refreshed || responseData.last_refreshed || null,
          insight,
          response: responseWithoutInsights,
          followUps,
          chartConfig: item.chart_config || responseData.chart_config || null,
          citations,
          sqlQueries,
          sourcesCount: citations.length + sqlQueries.length,
          createdAt,
          genieRawData: Array.isArray(responseData.genie_raw_data) ? responseData.genie_raw_data
              : null,
          isFeedbackActive: item.is_feedback_active === true,
          feedbackThumbs: item.thumbs || null,
        };
      }

      return null;
    })
    .filter(Boolean);
};
